// Single source of truth for the /memory assist metrics (pulse header + receipts). Computed at
// render from raw event/node fields, so a scope change reprices the retained history — no backfill.
//
// TWO metrics, deliberately different scopes:
//  • Turns-helped ("any-cited"): a turn counts if its answer cited ≥1 remembered fact. A loose
//    engagement signal ("memory showed up"), NOT the time-saved claim.
//  • Time-saved (floor): re-explaining time avoided, credited ONLY for facts native context
//    couldn't have carried to that turn — a fact you'd otherwise have re-typed. Charged once per
//    (session, fact) at the composition rate + a recognition floor. Conservative by construction.

const RECLARIFY_WPM = 19;   // Karat et al. 1999 — composition (generating new text), not transcription
const FACT_FLOOR_S = 10;    // per-fact "realize the context is missing" overhead

// Same-session dormancy gate (turns). A cited fact captured THIS session is credited only if it was
// captured ≥N turns before it was used — long enough that native context has rotted it out (a coding
// turn is 3-10k+ tokens, so 20 turns ≈ 60-200k intervening tokens, well past where semantic
// decision-facts, the fastest-rotting kind, degrade). Below N it was likely still in context, so
// memory added nothing (in-context echo). Cross-session facts skip this gate entirely.
export const DORMANT_TURNS = 20;

// The cited facts on ONE turn that native context wouldn't have carried here — the value on that turn.
//   xsession: captured in a DIFFERENT session (a different chat never had it).
//   xsegment: same session but captured before this session's compaction (GROUND TRUTH — the detail
//             was summarized away; from the backend's `cited_xsegment`, forward-only).
//   dormant:  same session, no compaction data, but captured ≥DORMANT_TURNS turns ago (the age PROXY
//             for rot — "earlier this session"). Only used when the compaction signal is absent.
// origin session is approximated by node.last_session (mutable last-touched; minor drift on old turns).
export function creditedOnTurn(ev, nodes) {
  const sid = ev?.session_id || "";
  const turn = ev?.turn || 0;
  const compacted = new Set(ev?.cited_xsegment || []);   // crossed a compaction (ground truth)
  const out = [];
  for (const nid of new Set(ev?.cited || [])) {
    const n = nodes?.[nid];
    if (!n) continue;
    if (n.last_session !== sid) { out.push({ id: nid, kind: "xsession" }); continue; }
    if (compacted.has(nid)) { out.push({ id: nid, kind: "xsegment" }); continue; }
    if (n.turn_added != null && (turn - n.turn_added) >= DORMANT_TURNS) out.push({ id: nid, kind: "dormant" });
  }
  return out;
}

// Aggregate over the retained event window. helpedTurns = any-cited; xsessionTurns = turns that
// pulled a cross-session fact; hoursSaved = the floor, de-duped once per (session, fact).
//
// `counter` (optional) is the backend's never-trimmed LIFETIME counter (B+): once the assist-log doc
// is seeded it carries {helped, xsession, credited_seconds}, computed by the SAME per-turn rule
// (memory_backend/assist_credit.py mirrors creditedOnTurn). When present it OVERRIDES the window sums
// so the numbers stay correct past the 1000-turn cap; when absent (old payload / not yet seeded) we
// fall back to the window recompute below — byte-identical to the pre-B+ behavior.
export function assistStats(events, nodes, counter) {
  const credited = new Set();
  let seconds = 0, helpedTurns = 0, xsessionTurns = 0;
  for (const e of events || []) {
    if ((e.cited || []).length) helpedTurns += 1;
    const facts = creditedOnTurn(e, nodes);
    if (facts.some((f) => f.kind === "xsession")) xsessionTurns += 1;
    const sid = e.session_id || "";
    for (const f of facts) {
      const key = `${sid}|${f.id}`;
      if (credited.has(key)) continue;
      credited.add(key);
      const w = (nodes?.[f.id]?.claim || "").trim().split(/\s+/).filter(Boolean).length;
      seconds += FACT_FLOOR_S + (60 * w) / RECLARIFY_WPM;
    }
  }
  return {
    helpedTurns: counter?.helped ?? helpedTurns,
    xsessionTurns: counter?.xsession ?? xsessionTurns,
    hoursSaved: (counter?.credited_seconds != null ? counter.credited_seconds : seconds) / 3600,
  };
}

// Kept for callers that only need the hours figure.
export function estHoursSaved(events, nodes) {
  return assistStats(events, nodes).hoursSaved;
}

// Format the (conservative) time-saved figure for display. Under-states by FLOORING to a coarse-
// but-honest granularity that widens as the number grows — fine early so a light user sees real
// value, coarse later to avoid false precision: <5 min shown to the exact minute, 5–60 min floored
// to 5-min steps, ≥1 hr floored to 30-min (0.5 hr) steps. The trailing "+" signals it's a floor.
// Shown as soon as there's ≥1 min saved (the old ≥0.5 hr gate hid genuine early value). Returns
// {show, value, unit} so the pulse header and the stat card can compose it consistently.
export function formatTimeSaved(hrs) {
  const mins = (hrs || 0) * 60;
  if (mins < 1) return { show: false, value: "", unit: "" };
  if (mins < 60) {
    const step = mins < 5 ? 1 : 5;                  // exact-minute under 5, else 5-min steps
    return { show: true, value: `~${Math.floor(mins / step) * step}+`, unit: "min" };
  }
  return { show: true, value: `~${Math.floor(hrs * 2) / 2}+`, unit: "hrs" };  // 30-min steps
}

// Count the distinct sessions the credited facts of one turn were last touched in — the honest basis
// for "pulled from N sessions". last_session is the MOST-RECENT session that modified a node, so
// this means "these facts were last touched across N of your sessions", not full lifetime provenance.
export function sessionsPulled(ev, nodes) {
  const set = new Set();
  for (const f of creditedOnTurn(ev, nodes)) {
    const s = nodes?.[f.id]?.last_session;
    if (s) set.add(s);
  }
  return set.size;
}
